import { useTeacherScope } from '@/lib/useTeacherScope';
import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getClasses, getSubjects, getTerms } from '@/api/owner';
import { getScores, postScoresImport } from '@/api/teacher';
import {
  Upload, AlertCircle, Check, X, FileText, Clipboard, Loader2,
} from 'lucide-react';
import { saveBlob } from '@/lib/native';

const arr = <T,>(src: any): T[] => {
  if (!src) return [];
  if (Array.isArray(src)) return src;
  if (Array.isArray(src.data)) return src.data;
  if (Array.isArray(src.data?.data)) return src.data.data;
  return [];
};
const unwrap = (r: any) => r?.data?.data ?? r?.data;

function parseCSV(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"' && text[i + 1] === '"') { cell += '"'; i++; }
      else if (c === '"') { inQuotes = false; }
      else { cell += c; }
    } else if (c === '"') { inQuotes = true; }
    else if (c === ',') { row.push(cell); cell = ''; }
    else if (c === '\n') { row.push(cell); rows.push(row); row = []; cell = ''; }
    else if (c === '\r') { /* skip */ }
    else { cell += c; }
  }
  if (cell || row.length) { row.push(cell); rows.push(row); }
  return rows.filter(r => r.some(x => x.trim() !== ''));
}

interface ParsedRow {
  admission_no: string;
  scores: Record<string, number | null>;
  errors: string[];
}

export function OwnerImportScoresPage() {
  const qc = useQueryClient();
  const [classId, setClassId] = useState<number | ''>('');
  const [subjectId, setSubjectId] = useState<number | ''>('');
  const [termId, setTermId] = useState<number | ''>('');
  const [csvText, setCsvText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [imported, setImported] = useState<{ count: number; rows: number } | null>(null);

  const classesQ = useQuery({ queryKey: ['owner-classes'], queryFn: () => getClasses({ per_page: 200 }).then(unwrap) });
  const subjectsQ = useQuery({ queryKey: ['owner-subjects'], queryFn: () => getSubjects({ per_page: 500 }).then(unwrap) });
  const termsQ = useQuery({ queryKey: ['owner-terms'], queryFn: () => getTerms().then(unwrap) });

  const scope = useTeacherScope();
  const classes = scope.classes(arr<any>(classesQ.data));
  const subjects = scope.subjects(arr<any>(subjectsQ.data), classId);
  const terms = arr<any>(termsQ.data);

  // Need columns + students for this class+subject+term to validate
  const gridQ = useQuery({
    queryKey: ['import-scores-grid', classId, subjectId, termId],
    queryFn: () => getScores({
      class_id: Number(classId),
      subject_id: Number(subjectId),
      term_id: Number(termId),
    }).then(unwrap),
    enabled: !!classId && !!subjectId && !!termId,
  });
  const grid = gridQ.data;
  const columns = arr<any>(grid?.columns);
  const students = arr<any>(grid?.students);

  // Parse CSV into rows with validation
  const parsed: ParsedRow[] = useMemo(() => {
    if (!csvText.trim() || !grid) return [];
    const rows = parseCSV(csvText);
    if (rows.length < 2) return [];

    const header = rows[0].map(h => h.trim());
    const admissionIdx = header.findIndex(h => /admission|reg|student.?id/i.test(h));
    if (admissionIdx < 0) return [];

    const columnCodeMap: Record<string, { id: number; max: number }> = {};
    columns.forEach((c: any) => { columnCodeMap[c.code.toUpperCase()] = { id: c.id, max: Number(c.max) }; });

    const studentByAdm: Record<string, any> = {};
    students.forEach((s: any) => { studentByAdm[String(s.admission_no).toUpperCase()] = s; });

    const out: ParsedRow[] = [];
    for (let r = 1; r < rows.length; r++) {
      const row = rows[r];
      const admNo = (row[admissionIdx] ?? '').trim();
      if (!admNo) continue;
      const errs: string[] = [];
      const scores: Record<string, number | null> = {};

      if (!studentByAdm[admNo.toUpperCase()]) {
        errs.push(`Admission no "${admNo}" not in this class`);
      }

      for (let hIdx = 0; hIdx < header.length; hIdx++) {
        if (hIdx === admissionIdx) continue;
        const code = header[hIdx].trim().toUpperCase();
        const colMeta = columnCodeMap[code];
        if (!colMeta) continue; // ignore columns that don't map to assessment components
        const raw = (row[hIdx] ?? '').trim();
        if (raw === '') { scores[code] = null; continue; }
        const n = Number(raw);
        if (!isFinite(n)) { errs.push(`${code}="${raw}" not a number`); scores[code] = null; }
        else if (n < 0) { errs.push(`${code} < 0`); scores[code] = null; }
        else if (n > colMeta.max) { errs.push(`${code} > max (${colMeta.max})`); scores[code] = null; }
        else { scores[code] = n; }
      }

      out.push({ admission_no: admNo, scores, errors: errs });
    }
    return out;
  }, [csvText, grid, columns, students]);

  const valid = parsed.filter(r => r.errors.length === 0);
  const invalid = parsed.filter(r => r.errors.length > 0);

  const importM = useMutation({
    mutationFn: () => postScoresImport({
      class_id: Number(classId),
      subject_id: Number(subjectId),
      term_id: Number(termId),
      rows: valid.map(r => ({ admission_no: r.admission_no, scores: r.scores })),
    }),
    onSuccess: (r: any) => {
      const d = unwrap(r);
      setImported({ count: d?.saved ?? d?.imported ?? valid.length, rows: d?.rows ?? valid.length });
      setCsvText('');
      setError(null);
      qc.invalidateQueries({ queryKey: ['scores-grid', classId, subjectId, termId] });
      qc.invalidateQueries({ queryKey: ['import-scores-grid', classId, subjectId, termId] });
    },
    onError: (e: any) => {
      const msg = e?.response?.data?.message
        || (e?.response?.data?.errors && Object.values(e.response.data.errors).flat().join(' · '))
        || 'Import failed';
      setError(String(msg));
    },
  });

  const sampleCsv = useMemo(() => {
    if (columns.length === 0) return 'admission_no,CA1,CA2,TEST,EXAM\n';
    const codes = columns.map((c: any) => c.code).join(',');
    return `admission_no,${codes}\n`;
  }, [columns]);

  const downloadTemplate = () => {
    // Build a CSV with all students from the current class so the user just fills scores
    if (!grid || students.length === 0) {
      alert('Select class + subject + term first to load students.');
      return;
    }
    const codes = columns.map((c: any) => c.code).join(',');
    const lines = [`admission_no,${codes}`];
    students.forEach((s: any) => {
      const vals = columns.map(() => '').join(',');
      lines.push(`${s.admission_no},${vals}`);
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    void saveBlob(blob, `scores-${grid.class?.replace(/\s+/g, '_')}-${grid.subject?.replace(/\s+/g, '_')}.csv`);
  };

  const handleFile = (f: File | null) => {
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setCsvText(String(reader.result ?? ''));
    reader.readAsText(f);
  };

  return (
    <div className="max-w-[1200px] mx-auto p-6 space-y-6">
      <div>
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Teaching tools</p>
        <h1 className="text-3xl font-extrabold text-slate-900">Import Scores</h1>
        <p className="text-slate-500 text-sm mt-1">
          Upload a CSV to fill the score grid in bulk. Invalid rows are rejected — nothing saves until every row is valid.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Class *</label>
            <select value={classId} onChange={(e) => setClassId(e.target.value ? Number(e.target.value) : '')}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
              <option value="">Choose class…</option>
              {classes.map((c: any) => <option key={c.id} value={c.id}>{c.name}{c.arm ? ` · ${c.arm}` : ''}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Subject *</label>
            <select value={subjectId} onChange={(e) => setSubjectId(e.target.value ? Number(e.target.value) : '')}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
              <option value="">Choose subject…</option>
              {subjects.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Term *</label>
            <select value={termId} onChange={(e) => setTermId(e.target.value ? Number(e.target.value) : '')}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
              <option value="">Choose term…</option>
              {terms.map((t: any) => <option key={t.id} value={t.id}>{t.name}{t.is_current ? ' (current)' : ''}</option>)}
            </select>
          </div>
        </div>
      </div>

      {gridQ.isLoading && <p className="text-sm text-slate-500">Loading grid…</p>}

      {grid && columns.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 space-y-3">
          <div className="flex justify-between items-center">
            <p className="text-sm font-semibold text-slate-900">
              {grid.class} · {grid.subject} · {grid.term} — expected columns
            </p>
            <button onClick={downloadTemplate}
              className="text-xs font-semibold text-[#0f172a] hover:underline">
              Download CSV template
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {columns.map((c: any) => (
              <span key={c.id} className="px-2 py-1 bg-slate-100 rounded text-xs font-mono">
                {c.code} <span className="text-slate-400">≤{c.max}</span>
              </span>
            ))}
          </div>
          <p className="text-xs text-slate-500">
            CSV columns must be: <span className="font-mono">admission_no,{columns.map((c: any) => c.code).join(',')}</span>
          </p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 whitespace-pre-wrap flex items-start gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {error}
        </div>
      )}

      {imported && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800 flex items-start gap-2">
          <Check className="w-4 h-4 flex-shrink-0 mt-0.5" />
          Imported {imported.count} score(s) across {imported.rows} row(s).
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="font-bold text-slate-900 flex items-center gap-2">
            <Upload className="w-4 h-4" /> Upload CSV
          </h2>
          <label className="text-xs font-semibold text-[#0f172a] cursor-pointer hover:underline">
            Choose file…
            <input type="file" accept=".csv,text/csv" className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0] ?? null)} />
          </label>
        </div>

        <div className="relative">
          <textarea
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
            placeholder={sampleCsv + 'OJP/26/0001,8,9,8,52\nOJP/26/0002,7,8,9,48\n…'}
            rows={10}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs font-mono"
          />
          <button
            onClick={async () => {
              try { const t = await navigator.clipboard.readText(); setCsvText(t); }
              catch { alert('Clipboard read denied — paste with Ctrl+V instead.'); }
            }}
            className="absolute top-2 right-2 px-2 py-1 text-xs bg-slate-900 text-white rounded font-semibold inline-flex items-center gap-1"
          >
            <Clipboard className="w-3 h-3" /> Paste
          </button>
        </div>

        {parsed.length > 0 && (
          <>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-xs font-semibold text-slate-600">Rows parsed</p>
                <p className="text-2xl font-extrabold text-slate-900">{parsed.length}</p>
              </div>
              <div className="bg-green-50 p-3 rounded-lg">
                <p className="text-xs font-semibold text-green-700">Valid</p>
                <p className="text-2xl font-extrabold text-green-800">{valid.length}</p>
              </div>
              <div className="bg-red-50 p-3 rounded-lg">
                <p className="text-xs font-semibold text-red-700">With errors</p>
                <p className="text-2xl font-extrabold text-red-800">{invalid.length}</p>
              </div>
            </div>

            <div className="border border-slate-200 rounded-lg max-h-[400px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 font-bold uppercase tracking-wider sticky top-0">
                  <tr>
                    <th className="px-2 py-2 text-left">Row</th>
                    <th className="px-2 py-2 text-left">Admission No.</th>
                    {columns.map((c: any) => (
                      <th key={c.id} className="px-2 py-2 text-right">{c.code}</th>
                    ))}
                    <th className="px-2 py-2 text-left">Errors</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {parsed.map((r, i) => (
                    <tr key={i} className={r.errors.length > 0 ? 'bg-red-50' : ''}>
                      <td className="px-2 py-1 font-mono">{i + 1}</td>
                      <td className="px-2 py-1 font-mono">{r.admission_no}</td>
                      {columns.map((c: any) => (
                        <td key={c.id} className="px-2 py-1 text-right font-mono">
                          {r.scores[c.code] ?? '—'}
                        </td>
                      ))}
                      <td className="px-2 py-1 text-red-700">
                        {r.errors.join('; ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button
              onClick={() => importM.mutate()}
              disabled={importM.isPending || valid.length === 0 || invalid.length > 0}
              className="w-full py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {importM.isPending ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Importing…</>
              ) : invalid.length > 0 ? (
                `Fix ${invalid.length} error row(s) to continue`
              ) : (
                `Import ${valid.length} row(s)`
              )}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
